/**
 * Change shipping address (replaces the CC Relocate flow).
 *
 * The page renders only the signed-in customer's own orders, so selecting the
 * order client-side from ?order= is safe — Shopify's session is the auth.
 *
 * With no ?order= (the thank-you page link cannot know the order number) it
 * opens their newest order that can still be changed and offers a "Different
 * order?" picker. An ?order= that can't be opened is a dead end on purpose --
 * falling back to another order got that order edited by mistake.
 *
 * IMPORTANT: the edit lock shown here is a HINT for the UI only. The backend
 * re-checks it authoritatively on save (ShipStation tag _exported_for_printing),
 * so a stale or spoofed client state cannot push through a change.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pfs-change-address]');
  if (!root) return;

  var ENDPOINT = (root.dataset.endpoint || '').replace(/\/+$/, '');
  var GMAPS_KEY = root.dataset.gmapsKey || '';

  // Theme-signed identity (see vdp::verifiedStorefrontCustomerId). Deliberately
  // not the customerData cookie -- that is minted by an endpoint which will
  // issue a token for any customer id to anyone who asks.
  var AUTH = {
    pfs_cid: root.dataset.cid || '',
    pfs_ts: root.dataset.ts || '',
    pfs_sig: root.dataset.sig || ''
  };
  function authQuery() {
    return '&pfs_cid=' + encodeURIComponent(AUTH.pfs_cid) +
           '&pfs_ts=' + encodeURIComponent(AUTH.pfs_ts) +
           '&pfs_sig=' + encodeURIComponent(AUTH.pfs_sig);
  }

  var ordersEl = root.querySelector('[data-pfs-orders]');
  var form = root.querySelector('[data-pfs-form]');
  var saveBtn = root.querySelector('[data-pfs-save]');
  var formError = root.querySelector('[data-pfs-formerror]');

  var SUBDIVISIONS = {
    US: [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['PR','Puerto Rico'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],['AA','Armed Forces Americas'],['AE','Armed Forces Europe'],['AP','Armed Forces Pacific']],
    CA: [['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],['NL','Newfoundland and Labrador'],['NT','Northwest Territories'],['NS','Nova Scotia'],['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],['SK','Saskatchewan'],['YT','Yukon']]
  };

  var order = null;
  var orders = [];   // candidate orders, newest first, each with .editable

  // Read by pfs-order-status.js on the account page -- see stashReturn().
  var RETURN_KEY = 'pfs_cha_return';
  // Orders older than this are previous seasons: long shipped, and asking the
  // backend about them would spend ShipStation calls on nothing.
  var RECENT_DAYS = 180;

  /* ---------------------------------------------------------------- utils */

  function showState(name) {
    root.querySelectorAll('[data-pfs-state]').forEach(function (el) {
      el.hidden = el.dataset.pfsState !== name;
    });
  }

  function setText(selector, value) {
    root.querySelectorAll(selector).forEach(function (el) { el.textContent = value; });
  }

  function field(name) {
    return form ? form.querySelector('[name="' + name + '"]') : null;
  }

  function param(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function renderAddress(target, addr, name) {
    if (!target) return;
    var lines = [
      name,
      addr.company,
      addr.address1,
      addr.address2,
      [addr.city, addr.province_code].filter(Boolean).join(' ') + ' ' + (addr.zip || ''),
      addr.country,
      addr.phone
    ].filter(function (l) { return l && String(l).trim(); });
    target.innerHTML = '<p>' + lines.map(function (l) {
      var d = document.createElement('div');
      d.textContent = l;
      return d.innerHTML;
    }).join('<br>') + '</p>';
  }

  /* ------------------------------------------------------------ lock state */

  /**
   * Decided from the page itself -- no backend call. This used to ask pfsweb
   * about every recent order before showing anything, which costs a Shopify
   * and a ShipStation lookup per order, in sequence; with several orders the
   * page sat on "Loading" for seconds (Dale, Oct 6 2026).
   *
   * - production_locked: pfsweb's reconcile cron copies ShipStation's
   *   _exported_for_printing onto the Shopify order every minute.
   * - fulfilled: both boxes have shipped.
   *
   * The save re-checks ShipStation authoritatively, so an order exported in the
   * last minute, or on hold / in error there, is refused at save instead.
   */
  function isEditable(o) {
    return o.fulfillment_status !== 'fulfilled' && o.production_locked !== true;
  }

  /* ----------------------------------------------------------------- form */

  function populateProvinces(countryCode, selected) {
    var sel = field('province_code');
    if (!sel) return;
    var list = SUBDIVISIONS[countryCode] || [];
    // A previous order's country may have turned the select into a text input;
    // switching to a US/CA order needs the select back.
    if (list.length && sel.tagName !== 'SELECT') {
      var rebuilt = document.createElement('select');
      rebuilt.name = 'province_code';
      rebuilt.id = sel.id;
      rebuilt.required = true;
      rebuilt.setAttribute('data-pfs-province', '');
      sel.parentNode.replaceChild(rebuilt, sel);
      sel = rebuilt;
    }
    sel.innerHTML = '';
    if (!list.length) {
      if (sel.tagName !== 'SELECT') { sel.value = selected || ''; return; }
      // Country we don't have subdivisions for — let them type it.
      var input = document.createElement('input');
      input.type = 'text';
      input.name = 'province_code';
      input.id = sel.id;
      input.value = selected || '';
      sel.parentNode.replaceChild(input, sel);
      return;
    }
    var blank = document.createElement('option');
    blank.value = '';
    blank.textContent = 'Select…';
    sel.appendChild(blank);
    list.forEach(function (pair) {
      var opt = document.createElement('option');
      opt.value = pair[0];
      opt.textContent = pair[1];
      if (pair[0] === selected) opt.selected = true;
      sel.appendChild(opt);
    });
  }

  function fillForm(o) {
    var a = o.address || {};
    // Name is the child's name and is not editable here.
    if (field('first_name')) field('first_name').value = a.first_name || '';
    if (field('last_name')) field('last_name').value = a.last_name || '';
    if (field('company')) field('company').value = a.company || '';
    if (field('address1')) field('address1').value = a.address1 || '';
    if (field('address2')) field('address2').value = a.address2 || '';
    if (field('city')) field('city').value = a.city || '';
    if (field('zip')) field('zip').value = a.zip || '';
    if (field('phone')) field('phone').value = a.phone || '';
    if (field('country')) field('country').value = a.country || '';
    if (field('country_code')) field('country_code').value = a.country_code || '';
    populateProvinces(a.country_code, a.province_code);
  }

  function snapshot() {
    var out = {};
    ['company', 'address1', 'address2', 'city', 'province_code', 'zip', 'phone'].forEach(function (k) {
      var el = field(k);
      out[k] = el ? el.value.trim() : '';
    });
    return out;
  }

  var pristine = null;

  function markDirty() {
    if (!saveBtn || !pristine) return;
    var now = snapshot();
    var dirty = Object.keys(now).some(function (k) { return now[k] !== pristine[k]; });
    saveBtn.disabled = !dirty;
    saveBtn.classList.toggle('is-dirty', dirty);
  }

  /* ------------------------------------------------------------ validation */

  var ZIP = { US: /^\d{5}(-\d{4})?$/, CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/ };

  function clearErrors() {
    root.querySelectorAll('[data-pfs-error-for]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
    if (formError) { formError.hidden = true; formError.textContent = ''; }
  }

  function showFieldError(name, message) {
    var el = root.querySelector('[data-pfs-error-for="' + name + '"]');
    if (el) { el.textContent = message; el.hidden = false; }
  }

  function validate(values, countryCode) {
    var ok = true;
    if (!values.address1) { showFieldError('address1', 'Please enter a street address.'); ok = false; }
    if (!values.city) { showFieldError('city', 'Please enter a city.'); ok = false; }
    if (SUBDIVISIONS[countryCode] && !values.province_code) {
      showFieldError('province_code', 'Please select a state.'); ok = false;
    }
    if (!values.zip) {
      showFieldError('zip', 'Please enter a zip code.'); ok = false;
    } else if (ZIP[countryCode] && !ZIP[countryCode].test(values.zip)) {
      showFieldError('zip', 'That zip code doesn’t look right.'); ok = false;
    }
    return ok;
  }

  /* ------------------------------------------------------- google places */

  /**
   * Places API (NEW), not the legacy google.maps.places.Autocomplete widget.
   *
   * Verified 2026-09-16 against the live key on packagefromsanta.com: the
   * legacy widget returns REQUEST_DENIED / ApiTargetBlockedMapError because the
   * key's API restriction list allows Places API (New) only, and Google no
   * longer offers the legacy API to projects created after March 2025.
   *
   * The new API hands back data rather than binding itself to the input, so the
   * dropdown below is ours. That is deliberate: it keeps the real <input> as
   * the form field, so manual typing, validation, snapshot() and markDirty()
   * all keep working exactly as before. Autocomplete stays a pure enhancement --
   * every failure path leaves the customer with a normal text box.
   */

  var placesLib = null;
  var placesSession = null;
  var acBox = null;          // the dropdown element
  var acSuggestions = [];    // current suggestions, parallel to the rendered rows
  var acActive = -1;         // keyboard-highlighted row
  var acTimer = null;

  var acStarted = false;

  function initAutocomplete() {
    // the picker can show the form more than once; load Maps only the first time
    if (acStarted || !GMAPS_KEY || !field('address1')) return;
    acStarted = true;
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(GMAPS_KEY) +
            '&libraries=places&loading=async&callback=pfsChaMapsReady';
    window.pfsChaMapsReady = function () {
      try {
        google.maps.importLibrary('places').then(function (lib) {
          placesLib = lib;
          attachAutocomplete();
        })['catch'](function () { /* enhancement only */ });
      } catch (e) { /* enhancement only */ }
    };
    document.head.appendChild(s);
  }

  function newSession() {
    try { placesSession = new placesLib.AutocompleteSessionToken(); } catch (e) { placesSession = null; }
  }

  function closeSuggestions() {
    acSuggestions = [];
    acActive = -1;
    if (!acBox) return;
    acBox.hidden = true;
    acBox.innerHTML = '';
    var input = field('address1');
    if (input) input.setAttribute('aria-expanded', 'false');
  }

  function highlight(i) {
    if (!acBox) return;
    var rows = acBox.children;
    acActive = i;
    for (var n = 0; n < rows.length; n++) {
      var on = (n === i);
      rows[n].classList.toggle('is-active', on);
      rows[n].setAttribute('aria-selected', on ? 'true' : 'false');
    }
  }

  function renderSuggestions(list) {
    acSuggestions = list;
    acActive = -1;
    if (!acBox) return;
    acBox.innerHTML = '';
    if (!list.length) { closeSuggestions(); return; }

    list.forEach(function (sug, i) {
      var pred = sug.placePrediction;
      var row = document.createElement('div');
      row.className = 'pfs-cha__ac-item';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', 'false');
      row.textContent = pred.text ? pred.text.toString() : '';
      // mousedown, not click: it fires before the input's blur closes the list
      row.addEventListener('mousedown', function (e) {
        e.preventDefault();
        choose(i);
      });
      acBox.appendChild(row);
    });

    acBox.hidden = false;
    var input = field('address1');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function choose(i) {
    var sug = acSuggestions[i];
    if (!sug || !sug.placePrediction) return;
    var place = sug.placePrediction.toPlace();
    place.fetchFields({ fields: ['addressComponents'] }).then(function () {
      applyPlace(place.addressComponents || []);
      closeSuggestions();
      // selecting a place ends the billing session; the next keystroke starts a new one
      newSession();
    })['catch'](function () { closeSuggestions(); });
  }

  function applyPlace(components) {
    var get = function (type, short) {
      var c = components.find(function (x) { return x.types && x.types.indexOf(type) !== -1; });
      if (!c) return '';
      return (short ? c.shortText : c.longText) || '';
    };

    var input = field('address1');
    var streetNumber = get('street_number');
    var route = get('route');
    // Only replace the typed address when Google actually gave us a house
    // number. Some suggestions resolve to a ROUTE rather than a street address
    // (verified 2026-09-16), and writing "Pebble Creek Court" over the
    // customer's "5050 Pebble Creek Ct" would silently delete their number.
    if (input && streetNumber) input.value = (streetNumber + ' ' + route).trim();

    if (field('city')) {
      // Township addresses -- common in Michigan, and PFS sees a lot of them --
      // come back with NO locality at all; the mailing city is
      // administrative_area_level_3 (e.g. "Shelby Township"). Verified against
      // live results 2026-09-16: 350 5th Ave NY has locality, 13863 Woodsett Ct
      // Shelby Township does not.
      field('city').value = get('locality')
        || get('postal_town')
        || get('sublocality_level_1')
        || get('sublocality')
        || get('administrative_area_level_3')
        || field('city').value;
    }
    if (field('zip')) field('zip').value = get('postal_code') || field('zip').value;

    var prov = get('administrative_area_level_1', true);
    var provEl = field('province_code');
    if (prov && provEl) provEl.value = prov;

    markDirty();
  }

  function requestSuggestions(q) {
    var countryCode = ((field('country_code') || {}).value || 'US').toLowerCase();
    if (!placesSession) newSession();

    placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      includedRegionCodes: [countryCode],
      sessionToken: placesSession
    }).then(function (res) {
      // ignore a response that arrived after the customer kept typing or left
      var input = field('address1');
      if (!input || document.activeElement !== input) return;
      renderSuggestions((res && res.suggestions) ? res.suggestions.slice(0, 5) : []);
    })['catch'](function () { closeSuggestions(); });
  }

  function attachAutocomplete() {
    var input = field('address1');
    if (!input || !placesLib || !placesLib.AutocompleteSuggestion) return;

    acBox = document.createElement('div');
    acBox.className = 'pfs-cha__ac';
    acBox.setAttribute('role', 'listbox');
    acBox.hidden = true;
    input.parentNode.appendChild(acBox);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (acTimer) clearTimeout(acTimer);
      if (q.length < 3) { closeSuggestions(); return; }
      acTimer = setTimeout(function () { requestSuggestions(q); }, 250);
    });

    input.addEventListener('keydown', function (e) {
      if (acBox.hidden || !acSuggestions.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight((acActive + 1) % acSuggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight((acActive <= 0 ? acSuggestions.length : acActive) - 1);
      } else if (e.key === 'Enter') {
        // only swallow Enter when a row is actually highlighted, so the form
        // can still be submitted normally
        if (acActive >= 0) { e.preventDefault(); choose(acActive); }
      } else if (e.key === 'Escape') {
        closeSuggestions();
      }
    });

    input.addEventListener('blur', function () { setTimeout(closeSuggestions, 150); });
  }

  /* ---------------------------------------------------------------- save */

  function save(e) {
    e.preventDefault();
    clearErrors();

    var values = snapshot();
    var countryCode = (field('country_code') || {}).value || '';
    if (!validate(values, countryCode)) return;

    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';

    fetch(ENDPOINT + '/vdp/changeShippingAddress?' + authQuery().slice(1), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        order_id: order.id,
        address: {
          company: values.company,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          province_code: values.province_code,
          zip: values.zip,
          phone: values.phone
        }
      })
    })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(function (body) {
        if (body && body.success) {
          var saved = body.address || values;
          saved.country = (field('country') || {}).value || '';
          // so switching away and back shows the new address, not the old one
          Object.keys(saved).forEach(function (k) { order.address[k] = saved[k]; });
          renderAddress(root.querySelector('[data-pfs-state="saved"] [data-pfs-current-address]'),
                        saved, [order.address.first_name, order.address.last_name].filter(Boolean).join(' '));
          showState('saved');
          return;
        }
        if (body && body.error_code === 'not_editable') {
          renderAddress(root.querySelector('[data-pfs-state="locked"] [data-pfs-current-address]'),
                        order.address, [order.address.first_name, order.address.last_name].filter(Boolean).join(' '));
          showState('locked');
          return;
        }
        throw new Error((body && body.error) || 'Something went wrong.');
      })
      .catch(function (err) {
        if (formError) {
          formError.textContent = err.message || 'We couldn’t save that change. Please try again.';
          formError.hidden = false;
        }
      })
      .finally(function () {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save changes';
        markDirty();
      });
  }

  /* ------------------------------------------------------ order selection */

  function hasShipTo(o) {
    // pre-order-only orders have no shipping address at all
    return !!(o && o.has_address !== false && o.address && o.address.address1);
  }

  function created(o) {
    var t = Number(o.created_ts) * 1000;
    return isFinite(t) ? t : 0;
  }

  function orderNumber(o) {
    return '#' + String(o.name || o.order_number || '').replace(/^#/, '');
  }

  function childName(o) {
    var a = o.address || {};
    return (o.child_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || '').trim();
  }

  function shipToName(o) {
    var a = o.address || {};
    return [a.first_name, a.last_name].filter(Boolean).join(' ');
  }

  function orderDate(o) {
    var t = created(o);
    if (!t) return '';
    try {
      var d = new Date(t);
      // year only when it isn't this one -- the picker is narrow on a phone
      var opts = { month: 'short', day: 'numeric' };
      if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
      return d.toLocaleDateString('en-US', opts);
    } catch (e) { return ''; }
  }

  // "#733862, package for Emma" -- follows the "Order " already in the heading.
  // The child's name (the Name attribute) only exists when the order has a
  // package; an items-only order ships to the parent, so it gets no "package".
  function headingFor(o) {
    if (o.child_name) return orderNumber(o) + ', package for ' + String(o.child_name).trim();
    var who = shipToName(o);
    return orderNumber(o) + (who ? ', shipping to ' + who : '');
  }

  function showNotFound(text) {
    var el = root.querySelector('[data-pfs-notfound-text]');
    if (el && text) el.textContent = text;
    var picker = root.querySelector('[data-pfs-picker]');
    if (picker) picker.hidden = true;
    showState('notfound');
  }

  /* -------------------------------------------------------- unsaved edits */

  var pendingOrder = null;   // where the picker wanted to go when we stopped it

  function formIsDirty() {
    var formState = root.querySelector('[data-pfs-state="form"]');
    if (!formState || formState.hidden || !pristine) return false;
    var now = snapshot();
    return Object.keys(now).some(function (k) { return now[k] !== pristine[k]; });
  }

  function setUnsaved(show) {
    var box = root.querySelector('[data-pfs-unsaved]');
    if (box) box.hidden = !show;
    if (!show) pendingOrder = null;
  }

  function switchTo(next) {
    setUnsaved(false);
    showOrder(next);
    setOrderParam(next.id);
  }

  function renderPicker() {
    var wrap = root.querySelector('[data-pfs-picker]');
    var sel = root.querySelector('[data-pfs-picker-select]');
    if (!wrap || !sel) return;
    if (orders.length < 2) { wrap.hidden = true; return; }

    sel.innerHTML = '';
    orders.forEach(function (o) {
      var opt = document.createElement('option');
      opt.value = String(o.id);
      // Padlock up front so a locked order still reads as locked when a phone
      // cuts the closed dropdown short; the note stays at the end (Dale, Oct 2026)
      opt.textContent = (o.editable ? '' : '\uD83D\uDD12 ') + [
        orderNumber(o),
        orderDate(o),
        childName(o),
        // Dale, Oct 2026: only a locked order carries a note
        o.editable ? '' : 'Cannot be changed'
      ].filter(Boolean).join(' · ');
      sel.appendChild(opt);
    });
    wrap.hidden = false;
  }

  function showOrder(o) {
    order = o;
    clearErrors();
    setText('[data-pfs-order-name]', headingFor(o));

    var sel = root.querySelector('[data-pfs-picker-select]');
    if (sel) sel.value = String(o.id);

    if (!o.editable) {
      renderAddress(root.querySelector('[data-pfs-state="locked"] [data-pfs-current-address]'),
                    o.address, shipToName(o));
      showState('locked');
      return;
    }

    fillForm(o);
    pristine = snapshot();
    markDirty();
    showState('form');
    initAutocomplete();
  }

  function showNothingEditable() {
    var list = root.querySelector('[data-pfs-order-list]');
    if (list) {
      list.innerHTML = '';
      orders.forEach(function (o) {
        var item = document.createElement('div');
        item.className = 'pfs-cha__orders-item';
        var title = document.createElement('p');
        title.className = 'pfs-cha__orders-title';
        title.textContent = [headingFor(o), orderDate(o)].filter(Boolean).join(' · ');
        var addr = document.createElement('div');
        addr.className = 'pfs-cha__current';
        item.appendChild(title);
        item.appendChild(addr);
        list.appendChild(item);
        renderAddress(addr, o.address, shipToName(o));
      });
    }
    var picker = root.querySelector('[data-pfs-picker]');
    if (picker) picker.hidden = true;
    showState('none');
  }

  function setOrderParam(id) {
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('order', String(id));
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (e) { /* URL stays as it was; the page still works */ }
  }

  /* --------------------------------------------------------------- sign in */

  /**
   * The OTP login app overwrites return_url with its own page URL, so after
   * signing in the customer lands on /account instead of back here. Remember
   * where they were going; pfs-order-status.js on the account page reads this
   * once and sends them on. Armed only when they actually click "sign in".
   */
  function stashReturn() {
    var link = root.querySelector('[data-pfs-signin]');
    if (!link) {
      // signed in: a stale stash must not bounce a later /account visit
      try { window.localStorage.removeItem(RETURN_KEY); } catch (e) {}
      return;
    }
    var dest = window.location.pathname + window.location.search;
    link.href = link.getAttribute('href').split('?')[0] + '?return_url=' + encodeURIComponent(dest);
    link.addEventListener('click', function () {
      try {
        window.localStorage.setItem(RETURN_KEY, JSON.stringify({ url: dest, t: Date.now() }));
      } catch (e) { /* storage blocked -- they land on /account, as before */ }
    });
  }

  /* ---------------------------------------------------------------- boot */

  function boot() {
    stashReturn();
    if (!ordersEl) { showState('notfound'); return; }

    var all;
    try { all = JSON.parse(ordersEl.textContent); } catch (e) { all = []; }

    var wanted = String(param('order') || '');
    var cutoff = Date.now() - RECENT_DAYS * 86400000;

    // Sort by date ourselves rather than trusting the payload order.
    orders = all
      .filter(function (o) {
        if (o.cancelled || !hasShipTo(o)) return false;
        // an explicitly requested order is always offered, however old
        return String(o.id) === wanted || created(o) >= cutoff;
      })
      .sort(function (a, b) {
        return (created(b) - created(a)) || (Number(b.order_number) - Number(a.order_number));
      });

    var requested = null;
    if (wanted) {
      requested = orders.find(function (o) { return String(o.id) === wanted; }) || null;
    }

    if (wanted && !requested) {
      // not theirs, cancelled, a pre-order with no ship-to, or junk
      showNotFound('We couldn’t find that order.');
      return;
    }

    if (!orders.length) {
      showNotFound('We couldn’t find an order with a shipping address on your account.');
      return;
    }

    // Fully fulfilled means both boxes have shipped: locked, no need to ask.
    orders.forEach(function (o) { o.editable = isEditable(o); });

    renderPicker();

    var pick = null;
    if (requested) {
      // an explicit, valid ?order= behaves exactly as before, locked or not
      showOrder(requested);
    } else if ((pick = orders.find(function (o) { return o.editable; }) || null)) {
      showOrder(pick);
      setOrderParam(pick.id);
    } else {
      showNothingEditable();
    }

    var pickerSelect = root.querySelector('[data-pfs-picker-select]');
    if (pickerSelect) {
      pickerSelect.addEventListener('change', function () {
        var next = orders.find(function (o) { return String(o.id) === pickerSelect.value; });
        if (!next) return;
        if (formIsDirty()) {
          // stay on the order being edited and ask
          pendingOrder = next;
          pickerSelect.value = String(order.id);
          setUnsaved(true);
          return;
        }
        switchTo(next);
      });
    }

    var unsavedSave = root.querySelector('[data-pfs-unsaved-save]');
    if (unsavedSave && form) {
      unsavedSave.addEventListener('click', function () {
        // saves THIS order and shows its confirmation; they can switch after
        setUnsaved(false);
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }

    var unsavedDiscard = root.querySelector('[data-pfs-unsaved-discard]');
    if (unsavedDiscard) {
      unsavedDiscard.addEventListener('click', function () {
        if (pendingOrder) switchTo(pendingOrder);
      });
    }

    // typing again means they chose to keep editing this order
    if (form) form.addEventListener('input', function () { setUnsaved(false); });
  }

  if (form) {
    form.addEventListener('input', markDirty);
    form.addEventListener('change', markDirty);
    form.addEventListener('submit', save);
  }

  boot();
})();
